import { ChatShowcase, Metadata } from "@dropbox/dash-component-library";
import { Facepile } from "@dropbox/dig-components/avatar";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Atoms, Box, Split } from "@dropbox/dig-foundations";
import { DashFacepile } from "@mirage/mosaics/DCLWrappers/DashFacepile";
import { getTimeAgoStringFromTimestamp } from "@mirage/search/SearchResults/util/resultUtil";
import { getInitials } from "@mirage/shared/account";
import { SearchResultIcon } from "@mirage/shared/icons";
import EventIcon from "@mirage/shared/icons/EventIcon";
import { FileContentIcon } from "@mirage/shared/icons/FileContentIcon";
import { RESULT_BADGE_ICON_SIZE } from "@mirage/shared/icons/util";
import i18n from "@mirage/translations";
import * as React from "react";
import styles from "./InfoPanel.module.css";

import type { dash, stacks } from "@dropbox/api-v2-client";
import type { ContentInfoPanelProps } from "@dropbox/dash-component-library";
import type { TruncateTooltipControlProps } from "@dropbox/dig-components/truncate";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type {
  AuthorInfo,
  SearchResult,
} from "@mirage/shared/search/search-result";
import type { StackItemUser } from "@mirage/shared/search/stack-item";

export const BASE_PREVIEW_ICON_SIZE = 72;
export const BASE_PREVIEW_ICON_CONTENT_SIZE = 32;
const FACEPILE_MAX_SIZE = 5;
export const SIDE_PANEL_TOOLTIP_PROPS: TruncateTooltipControlProps = {
  auto: true,
  placement: "top",
  openDelay: 400,
};

// Helper function to extract profile image URL from either type
const getProfileImageUrl = (
  user: dash.Author | stacks.StackSharingSubjectUser,
): string | undefined => {
  if ("profile_image_url" in user) {
    return user.profile_image_url;
  }
  if ("profile_photo_url" in user) {
    return user.profile_photo_url;
  }
  return undefined;
};

export const AttributeValueText = ({ value }: { value: React.ReactNode }) => {
  return <Text variant="label">{value}</Text>;
};

export const getUserFacePileItem = (
  user: dash.Author | stacks.StackSharingSubjectUser,
) => {
  const { display_name, email } = user;
  const profileImageUrl = getProfileImageUrl(user);
  return (
    <Facepile.Item
      key={email}
      src={profileImageUrl}
      alt={i18n.t("avatar_alt_text", {
        name: display_name ?? email,
      })}
    >
      {getInitials(display_name || email || "")}
    </Facepile.Item>
  );
};

export const FacePileAttribute = ({
  users,
  countText,
}: {
  users: dash.Author[] | stacks.StackSharingSubjectUser[];
  countText: string;
}) => {
  return (
    <Split gap="8">
      <Split.Item>
        <DashFacepile
          members={users}
          size="xsmall"
          maxMembersToShow={FACEPILE_MAX_SIZE}
        />
      </Split.Item>
      <Split.Item>
        <AttributeValueText value={countText} />
      </Split.Item>
    </Split>
  );
};

export const AvatarDisplayNameMetadata = ({
  user,
}: {
  user: AuthorInfo | StackItemUser;
}) => {
  const { displayName, email, profilePhotoUrl } = user;
  return (
    <Metadata size="small">
      <Metadata.Item>
        <Metadata.Avatar
          src={profilePhotoUrl}
          alt={i18n.t("avatar_alt_text", {
            name: displayName ?? email,
          })}
        >
          {getInitials(displayName || email || "")}
        </Metadata.Avatar>
        <Metadata.Label>{displayName}</Metadata.Label>
      </Metadata.Item>
    </Metadata>
  );
};

export const ContentPreview = ({
  previewUrl,
  altText,
}: {
  previewUrl: string;
  altText: string;
}) => {
  return (
    <Box
      marginX="Macro Large"
      display="flex"
      justifyContent="center"
      borderRadius="Large"
      boxShadow="Raised"
      overflowY="hidden"
      className={styles.previewImageContainer}
    >
      <img
        src={previewUrl}
        alt={i18n.t("content_preview_alt_text", { title: altText })}
        className={styles.previewImage}
      />
    </Box>
  );
};

export const PreviewIconContainer = ({
  icon,
  backgroundColor = "Primary Surface",
}: {
  icon: React.ReactNode;
  backgroundColor?: Atoms["backgroundColor"];
}) => {
  return (
    <Box
      width="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      backgroundColor={backgroundColor}
      style={{ height: "148px" }}
    >
      {icon}
    </Box>
  );
};

export const getChatPreview = (
  inputExample: string,
  subtitle: string,
): Partial<ContentInfoPanelProps> => {
  // Prevent showcase animations to avoid
  // confusing users from expecting a response
  return {
    title: undefined,
    subtitle: undefined,
    contentPreview: (
      <Box width="100%" paddingRight="16" paddingLeft="16">
        <ChatShowcase inputText={inputExample} animate={false} />
      </Box>
    ),
    previewSubtitle: subtitle,
    align: "center",
  };
};

export const getCalendarEventInfo = (
  event: SearchResult,
): Partial<ContentInfoPanelProps> => {
  const attributes = [];

  if (event.location) {
    const { location } = event;
    attributes.push({
      label: i18n.t("where_label"),
      value: (
        <Box style={{ maxWidth: "70%" }}>
          <AttributeValueText
            value={
              <Truncate tooltipControlProps={SIDE_PANEL_TOOLTIP_PROPS}>
                {location}
              </Truncate>
            }
          />
        </Box>
      ),
    });
  }

  if (event.attendees?.length && event.attendees.length > 0) {
    const { attendees } = event;
    attributes.push({
      label: i18n.t("guests_label"),
      value: (
        <FacePileAttribute
          users={attendees}
          countText={i18n.t("num_guests", { count: attendees.length })}
        />
      ),
    });
  }

  return {
    contentPreview: event.startTime ? (
      <EventIcon
        eventDate={new Date(event.startTime)}
        size="3xlarge"
        sourceIcon={
          <SearchResultIcon result={event} size={RESULT_BADGE_ICON_SIZE} />
        }
      />
    ) : (
      <SearchResultIcon result={event} size={BASE_PREVIEW_ICON_SIZE} />
    ),
    attributes,
  };
};

export const getContentInfo = (
  content: SearchResult | Recommendation,
): Partial<ContentInfoPanelProps> => {
  const attributes = [];

  if (content.filteredModifier?.modifiedAtMs || content.providerUpdateAtMs) {
    const { filteredModifier, providerUpdateAtMs } = content;
    const { modifiedAtMs } = filteredModifier ?? {};
    attributes.push({
      label: i18n.t("last_updated_label"),
      value: (
        <AttributeValueText
          value={getTimeAgoStringFromTimestamp(
            modifiedAtMs ?? providerUpdateAtMs,
          )}
        />
      ),
    });
  }

  if (content.connectorInfo) {
    const { displayName } = content.connectorInfo;
    attributes.push({
      label: i18n.t("found_in_label"),
      value: (
        <Metadata>
          <Metadata.Item>
            <Metadata.ArbitraryIcon
              src={<SearchResultIcon result={content} size={16} />}
            />
            <Metadata.Label>{displayName}</Metadata.Label>
          </Metadata.Item>
        </Metadata>
      ),
    });
  }

  if (
    content.lastModifier &&
    (content.lastModifier.displayName || content.lastModifier.email)
  ) {
    attributes.push({
      label: i18n.t("updated_by_label"),
      value: <AvatarDisplayNameMetadata user={content.lastModifier} />,
    });
  }

  return {
    contentPreview: content.previewUrl ? (
      <ContentPreview previewUrl={content.previewUrl} altText={content.title} />
    ) : (
      <FileContentIcon
        content={content}
        size="3xlarge"
        hasBackground={true}
        constrainContent={false}
        contentPadding="16"
      />
    ),
    attributes,
  };
};
